import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';

import { OwnedCardRow } from '../OwnedCardRow';
import { LazyAllLastOwnedCardsFragment } from '../fragments';
import type {
  LazyAllLastOwnedCardsBySo5CompetitionQuery,
  LazyAllLastOwnedCardsBySo5CompetitionQueryVariables,
} from './__generated__/index.graphql';

const LAZY_ALL_LAST_OWNED_CARDS_BY_SO5_COMPETITION_QUERY = gql`
  query LazyAllLastOwnedCardsBySo5CompetitionQuery(
    $slug: String!
    $after: String
    $inSeasonEligible: Boolean!
    $classicOnly: Boolean!
  ) {
    so5 {
      so5Competition(slug: $slug) {
        slug
        cards(
          ownedByMe: true
          after: $after
          first: 20
          inSeasonEligible: $inSeasonEligible
          classicOnly: $classicOnly
          sorts: [RECENTLY_OWNED_FIRST]
        ) {
          ...LazyAllLastOwnedCardsQuery_anyCard
        }
      }
    }
  }
  ${LazyAllLastOwnedCardsFragment}
` as TypedDocumentNode<
  LazyAllLastOwnedCardsBySo5CompetitionQuery,
  LazyAllLastOwnedCardsBySo5CompetitionQueryVariables
>;

const AllLastOwnedCardsRoot = styled(Vertical)`
  width: 280px;
  max-height: 400px;
  overflow: auto;
`;

const AllLastOwnedCardsTitle = styled(LabelS)`
  background-color: var(--c-nd-50);
  padding: var(--half-unit) var(--unit);
  border-radius: var(--unit);
`;

type Props = {
  so5CompetitionSlug: string;
  displayName: string;
  count: number;
  seasonality: So5LeaderboardSeasonality;
  seasonalityName: string;
};

export const LazyAllLastOwnedCardsBySo5Competition = ({
  so5CompetitionSlug,
  displayName,
  count,
  seasonality,
  seasonalityName,
}: Props) => {
  const { data, loading, loadMore } = usePaginatedQuery(
    LAZY_ALL_LAST_OWNED_CARDS_BY_SO5_COMPETITION_QUERY,
    {
      variables: {
        slug: so5CompetitionSlug,
        inSeasonEligible: seasonality === So5LeaderboardSeasonality.IN_SEASON,
        classicOnly: seasonality === So5LeaderboardSeasonality.ALL_SEASONS,
      },
      connection: 'AnyCardInterfaceConnection',
    }
  );

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        after: data?.so5?.so5Competition?.cards?.pageInfo.endCursor,
      });
    },
    !!data?.so5?.so5Competition?.cards?.pageInfo.hasNextPage,
    loading
  );

  const { cards } = data?.so5?.so5Competition || {
    cards: { nodes: Array.from({ length: count }) },
  };

  return (
    <AllLastOwnedCardsRoot>
      <Vertical gap={2}>
        <AllLastOwnedCardsTitle className="text-center" color="var(--c-nd-600)">
          <FormattedMessage
            id="marketplace.benchObject.lastOwnedCardsBySo5Competition.title"
            defaultMessage="You own x{totalCount, number} {so5CompetitionName} {seasonalityName} {totalCount, plural, one {card} other {cards}}"
            values={{
              totalCount: count,
              so5CompetitionName: displayName,
              seasonalityName,
            }}
          />
        </AllLastOwnedCardsTitle>
        <Vertical>
          {cards?.nodes?.length > 0 && (
            <>
              {cards.nodes.map((c, index) => (
                <Skeleton key={c?.slug || index} loading={!data}>
                  <OwnedCardRow anyCard={c} />
                </Skeleton>
              ))}
            </>
          )}
          {!!data && <InfiniteScrollLoader />}
        </Vertical>
      </Vertical>
    </AllLastOwnedCardsRoot>
  );
};
